import { MyLiNKQueryKeys } from "constants/my-link-query-keys";
import { useQuery } from "react-query";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { t } from "utilities/localization-utils";
import UserCollectionBookmarkService from "utilities/services/user-collection-bookmarks/user-collection-bookmark-service";
import UserCollectionService from "utilities/services/users/user-collections/user-collection-service";
import { ToastManager } from "utilities/toast/toast-manager";

const useBookmarksAndCollections = (bookmarkId: number) => {
    const { currentIdentity } = useCurrentIdentity();

    const { data: currentCollections, refetch: refetchCurrentCollections } =
        useQuery({
            queryKey: [MyLiNKQueryKeys.CollectionsByBookmarkId, bookmarkId],
            queryFn: async () =>
                await fetchCollections(currentIdentity?.userId()!, bookmarkId),
            enabled: !!currentIdentity?.userId(),
            placeholderData: [],
        });

    const { data: allCollections, refetch: refetchAllCollections } = useQuery({
        queryKey: [MyLiNKQueryKeys.AllCollections],
        queryFn: async () => await fetchCollections(currentIdentity?.userId()!),
        enabled: !!currentIdentity?.userId(),
        placeholderData: [],
    });

    const {
        data: userCollectionsBookmarks,
        refetch: refetchUserCollectionsBookmarks,
    } = useQuery({
        queryKey: [MyLiNKQueryKeys.UserCollectionsBookmarks],
        queryFn: async () => await fetchCollectionsBookmarks(bookmarkId),
        enabled: !!currentIdentity?.userId(),
        placeholderData: [],
    });

    const refetchAll = async () => {
        await Promise.all([
            refetchAllCollections(),
            refetchCurrentCollections(),
            refetchUserCollectionsBookmarks(),
        ]);
    };

    return {
        userCollectionsBookmarks: userCollectionsBookmarks ?? [],
        currentCollections: currentCollections ?? [],
        allCollections: allCollections ?? [],
        refetchAll,
    };
};

const fetchCollections = async (userId: number, bookmarkId?: number) => {
    try {
        const query = bookmarkId ? { bookmarkId: Number(bookmarkId) } : {};
        const result = await UserCollectionService.list(
            {
                userId,
            },
            query
        );
        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return result.resultObjects;
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("collections"),
            })
        );
    }
};

const fetchCollectionsBookmarks = async (bookmarkId: number) => {
    try {
        const result = await UserCollectionBookmarkService.list({
            bookmarkId,
        });
        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return result.resultObjects;
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("collections"),
            })
        );
    }
};

export default useBookmarksAndCollections;
